import { ApolloLink } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { datadogRum } from '@datadog/browser-rum'
import { BrowserRouter } from 'react-router-dom'
import { match } from 'ts-pattern'

import { FatalErrorBoundary, RedwoodProvider } from '@cedarjs/web'
import {
  GraphQLClientConfigProp,
  RedwoodApolloProvider,
} from '@cedarjs/web/apollo'
import { toast } from '@cedarjs/web/toast'

import { cacheConfig } from 'src/apollo/cacheConfig'
import { retryIf, statusCodeToErrorMessage } from 'src/apollo/retryIf'
import { useLogOutAndSyncTabs } from 'src/hooks/useLogOutAndSyncTabs/useLogOutAndSyncTabs'
import { restoreSettings } from 'src/lib/settings'
import FatalErrorPage from 'src/pages/FatalErrorPage'
import { FeaturebaseWidgetProvider } from 'src/providers/context/FeaturebaseWidgetContext'
import { LocalSettingsProvider } from 'src/providers/context/LocalSettingsContext'
import { PylonChatWidgetProvider } from 'src/providers/context/PylonChatWidgetContext'
import Routes from 'src/Routes'
import TimedSession from 'src/wrappers/TimedSession'

import { AuthProvider, useAuth } from './auth'
import { useSyncUserToDatadog } from './hooks/useSyncUserToDatadog/useSyncUserToDatadog'
import 'src/lib/pdfmeGlobals'
import 'src/lib/preventIosFocusZoom'
import 'src/lib/reloadOnStaleChunk'
import 'src/scaffold.css'
import 'src/index.css'
import { appType } from './utils/appType'

const App = () => {
  if (process.env.DATADOG_RUM_ENABLED === 'true') {
    datadogRum.init({
      applicationId: process.env.DATADOG_RUM_APPLICATION_ID!,
      clientToken: process.env.DATADOG_RUM_CLIENT_TOKEN!,
      site: 'us5.datadoghq.com',
      service: match(appType)
        .with('emr', () => 'develo-emr')
        .with('patientPortal', () => 'develo-emr')
        .with('admin', () => 'develo-admin')
        .exhaustive(),
      env: process.env.DATADOG_RUM_ENV,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: process.env.FC_GIT_COMMIT_SHA || '0.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'allow',
      allowedTracingUrls: [process.env.REDWOOD_API_URL!],
    })

    datadogRum.startSessionReplayRecording()
  }
  return (
    <FatalErrorBoundary page={FatalErrorPage}>
      <RedwoodProvider titleTemplate="%PageTitle">
        <AuthProvider>
          <AppApollo />
        </AuthProvider>
      </RedwoodProvider>
    </FatalErrorBoundary>
  )
}

/** These mutations handle errors locally, with custom toasts or inline feedback. */
const MUTATIONS_TO_TOAST_NOTHING = [
  'CreateDirectPatientCharge',
  'CreatePaperStatement',
  'CreateLabPanel',
  'UpsertMacroPhrase',
  'UpdatePatientCaregiverOptOutMutation',
  'DuplicateVisitTemplate',
  'SplitDocumentCasePdf',
  'UpdateAppointmentTranscript',
  'AddPhysicalExamImage',
  'QueuePullPatientFromDoctorFirst',
  'GetUpdatedDoctorFirstNotifications',
  'RequestPortalBillingStatementPdf',
  'RequestStaffBillingStatementPdf',
  'RequestPaymentNotificationStatementPdf',
]

const MAX_RETRY_ATTEMPTS = 3
const MUTATIONS_TO_RETRY: string[] = []

const AppApollo = () => {
  const logOutAndSyncTabs = useLogOutAndSyncTabs()

  useSyncUserToDatadog()

  const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
    if (networkError) {
      // oxlint-disable-next-line no-console
      console.error('Network error', networkError)
      const statusCode = (networkError as { statusCode?: number }).statusCode
      toast.error(
        statusCode && statusCodeToErrorMessage[statusCode]
          ? statusCodeToErrorMessage[statusCode]
          : 'Network error. Please try again.'
      )
    }

    const isMutation = operation.query.definitions.some(
      (definition) =>
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'mutation'
    )
    if (!graphQLErrors) return
    if (
      graphQLErrors.some(
        ({ extensions }) => extensions?.code === 'SESSION_EXPIRED'
      )
    ) {
      // oxlint-disable-next-line no-console
      console.error('Session expiration detected in error link. Logging out.')
      toast.error('Session expired. Please login again.')
      logOutAndSyncTabs()
    } else if (
      graphQLErrors.some(
        ({ extensions }) => extensions?.code === 'IP_RESTRICTED'
      )
    ) {
      toast.error('Remote access not allowed.')
      logOutAndSyncTabs()
    } else if (
      graphQLErrors.some(({ extensions }) => extensions?.code === 'FORBIDDEN')
    ) {
      toast.error('Permission denied')
    } else if (isMutation) {
      if (MUTATIONS_TO_TOAST_NOTHING.includes(operation.operationName)) {
        // toast nothing — these mutations handle errors locally
      } else {
        const errorMessages = graphQLErrors.map(({ message }) => message)

        for (const message of errorMessages) {
          toast.error(message)
        }
      }
    }
  })
  // const { uri } = useFetchConfig()
  const graphQLClientConfig: GraphQLClientConfigProp = {
    connectToDevTools: Boolean(process.env.APOLLO_DEV_TOOLS_ACTIVE),
    httpLinkConfig: { credentials: 'include' },
    link(rwLinks) {
      const links = rwLinks.map(({ link }) => link)
      const httpLink = links.pop()
      // const restLink = new RestLink({
      //   uri: uri.replace('/graphql', ''),
      //   credentials: 'include',
      // })

      const retryLink = new RetryLink({
        attempts: {
          max: MAX_RETRY_ATTEMPTS,
          retryIf: retryIf(MUTATIONS_TO_RETRY),
        },
      })

      return ApolloLink.from([
        ...links,
        errorLink,
        retryLink,
        // restLink,
        httpLink!,
      ])
    },
    cacheConfig,
  }

  return (
    <RedwoodApolloProvider
      useAuth={useAuth}
      graphQLClientConfig={graphQLClientConfig}
    >
      <LocalSettingsProvider settings={restoreSettings()}>
        <FeaturebaseWidgetProvider>
          <PylonChatWidgetProvider>
            <TimedSession>
              <BrowserRouter>
                <Routes />
              </BrowserRouter>
            </TimedSession>
          </PylonChatWidgetProvider>
        </FeaturebaseWidgetProvider>
      </LocalSettingsProvider>
    </RedwoodApolloProvider>
  )
}

export default App
